/**
 * Executive View theme — the "Duplo" scale.
 *
 * Nested on top of the app theme via MUI's createTheme(theme, overrides)
 * deep-merge, applied only inside the ExecutiveView route (the app-wide
 * ThemeProvider in src/index.tsx stays untouched). The executive surface
 * deliberately runs larger type and roomier controls than the analyst UI,
 * whose IDE-like density is equally deliberate — don't "unify" the two.
 *
 * Dialogs/popovers launched from inside ExecutiveView portal out of the
 * DOM but inherit this theme through the React tree — desired.
 */
import { createTheme, Theme } from '@mui/material/styles';
// The app remaps MUI's class prefix (Mui → Trinity) for component slot
// classes (src/MuiClassNameSetup.ts), so a hardcoded ".MuiInputLabel-
// shrink" selector matches nothing at runtime — inputLabelClasses tracks
// the real generated name, same pattern as Sidebar.tsx's chipClasses use.
import { inputLabelClasses } from '@mui/material/InputLabel';

import { theme as baseTheme } from 'src/theme';

export const executiveTheme: Theme = createTheme(baseTheme, {
  typography: {
    body1: { fontSize: '1.0625rem', lineHeight: 1.55 },
    body2: { fontSize: '0.9375rem', lineHeight: 1.5 },
    subtitle1: { fontSize: '1.125rem' },
    subtitle2: { fontSize: '1rem' },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
  },
  components: {
    MuiButton: {
      defaultProps: {
        // App default is 'small'; executives get full-size targets.
        size: 'medium',
      },
      styleOverrides: {
        root: {
          fontSize: '0.9375rem',
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        size: 'medium',
      },
    },
    // What-If Control Bar floating labels (design doc EXEC_WHATIF_v1_
    // design.md §3): MUI's shrunk (floated) label inherits caption size
    // by default (0.75rem in the base theme) — a step too small for this
    // audience once it's carrying the sentence's grammar ("What happens
    // to" / "if we change"). Bumped one step up, still weight 400 (the
    // placeholder-text state) per the exec two-weight rule (400/600 only
    // — this is not a 600 label, it's body copy that happens to float).
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.9375rem',
          [`&.${inputLabelClasses.shrink}`]: {
            fontSize: '0.8125rem',
          },
        },
      },
    },
  },
});
